import { Lock, LockOpen } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getThreadFlagStrings, getThreadLockReason, humanizeLockReason } from "@/lib/thread-flags";
import { cn } from "@/lib/utils";

/**
 * The AI-handling banner above the conversation (design 01-inbox). Originally ONE row that
 * split in half when BOTH `locked` and `staff_handles` were set — left half violet Locked
 * (with Unlock), right half emerald Staff handles (with "Done for now" + clear).
 *
 * DISABLED (for now): the staff_handles half — including the "Done for now" / "Marked done"
 * acknowledge — is hidden; the flag is no longer applied anywhere and assignment lives on
 * tasks only. Only the Locked half renders (full-width). The commented code below is kept
 * for easy restore.
 */
export function AiHandlingBanner({
  thread,
  threadTasks = [],
  onThreadFlagChange /* , onAcknowledgeMessage */,
}) {
  const tid = String(thread?.threadId || thread?.id || "").trim();
  const flags = getThreadFlagStrings(thread);
  const isLocked = flags.includes("locked");
  // DISABLED (for now): staff_handles half.
  // const isStaffHandles = flags.includes("staff_handles");
  if (!tid || !isLocked) return null;

  // const both = isLocked && isStaffHandles;
  const lockReason = getThreadLockReason(thread);
  // Locking a thread only creates an auto task when the bot resolved a specific assignee
  // (see internal/autotask); a generic lock with no assignee creates no task at all.
  const hasAutoTask = threadTasks.some((t) => t?.source === "auto");
  // const assignee = getThreadAssignee(thread);
  // const me = String(getAuthUser()?.id || "").trim();
  // const staffWho = assignee ? (assignee.id === me ? "you" : assignee.name) : "";
  // const acknowledged = Boolean(thread?.lastMessageAcknowledgedAt);
  // const lastMessageId = String(thread?.lastMessageId || "").trim();

  const lockedHalf = (
    <div
      className={cn(
        "flex min-w-0 items-center gap-2.5 px-4 py-2",
        "bg-gradient-to-r from-violet-100/80 to-violet-50/30 text-violet-950 dark:from-violet-950/45 dark:to-violet-900/15 dark:text-violet-50",
      )}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-violet-500/15 ring-1 ring-violet-500/30 dark:bg-violet-500/20 dark:ring-violet-400/40">
        <Lock className="h-3.5 w-3.5 text-violet-700 dark:text-violet-300" strokeWidth={2.5} aria-hidden />
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-xs font-semibold tracking-tight">
          Locked
          {lockReason ? (
            <>
              {" · "}
              <span className="font-mono text-[11px] font-medium">{humanizeLockReason(lockReason)}</span>
            </>
          ) : null}
        </p>
        <p className="truncate text-[10px] text-violet-800/80 dark:text-violet-200/70">
          {hasAutoTask ? "AI paused — created a task." : "AI paused."}
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          void onThreadFlagChange(tid, "locked", false);
        }}
        className="h-7 shrink-0 border-violet-400/60 bg-background/70 px-2.5 text-[11px] font-semibold text-violet-900 shadow-sm transition hover:border-violet-500/80 hover:bg-violet-200/70 hover:text-violet-950 dark:border-violet-400/50 dark:bg-violet-950/40 dark:text-violet-100 dark:hover:bg-violet-900/60"
      >
        <LockOpen className="mr-1 h-3 w-3" strokeWidth={2.5} aria-hidden />
        Unlock
      </Button>
    </div>
  );

  // DISABLED (for now): the emerald staff_handles half — "Done for now" acknowledges the
  // last message ("Marked done — returns to “Needs you” on the next message."), the X
  // clears the flag. Restore alongside the staff_handles feature.
  //
  // const staffHalf = (
  //   <div
  //     className={cn(
  //       "flex min-w-0 items-center gap-2.5 px-4 py-2",
  //       "bg-gradient-to-l from-emerald-100/80 to-emerald-50/30 text-emerald-950 dark:from-emerald-950/45 dark:to-emerald-900/15 dark:text-emerald-50",
  //     )}
  //   >
  //     <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-emerald-500/15 ring-1 ring-emerald-500/30 dark:bg-emerald-500/20 dark:ring-emerald-400/40">
  //       <UserRoundCheck
  //         className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-300"
  //         strokeWidth={2.5}
  //         aria-hidden
  //       />
  //     </span>
  //     <div className="min-w-0 flex-1 leading-tight">
  //       <p className="truncate text-xs font-semibold tracking-tight">
  //         Staff handles
  //         {staffWho ? ` · ${staffWho}` : ""}
  //       </p>
  //       <p className="truncate text-[10px] text-emerald-800/80 dark:text-emerald-200/70">
  //         {acknowledged
  //           ? "Marked done — returns to “Needs you” on the next message."
  //           : "AI stays paused while you reply."}
  //       </p>
  //     </div>
  //     {!acknowledged && lastMessageId ? (
  //       <Button
  //         type="button"
  //         size="sm"
  //         variant="outline"
  //         onClick={() => {
  //           void onAcknowledgeMessage(tid, lastMessageId);
  //         }}
  //         className="h-7 shrink-0 border-emerald-400/60 bg-background/70 px-2.5 text-[11px] font-semibold text-emerald-900 shadow-sm transition hover:border-emerald-500/80 hover:bg-emerald-200/70 hover:text-emerald-950 dark:border-emerald-400/50 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-900/60"
  //       >
  //         <Check className="mr-1 h-3 w-3" strokeWidth={2.5} aria-hidden />
  //         Done for now
  //       </Button>
  //     ) : null}
  //     <Button
  //       type="button"
  //       size="sm"
  //       variant="outline"
  //       title="Clear the staff-handles flag"
  //       aria-label="Clear the staff-handles flag"
  //       onClick={() => {
  //         void onThreadFlagChange(tid, "staff_handles", false);
  //       }}
  //       className="h-7 w-7 shrink-0 border-emerald-400/60 bg-background/70 p-0 text-emerald-900 shadow-sm transition hover:border-emerald-500/80 hover:bg-emerald-200/70 hover:text-emerald-950 dark:border-emerald-400/50 dark:bg-emerald-950/40 dark:text-emerald-100 dark:hover:bg-emerald-900/60"
  //     >
  //       <X className="h-3 w-3" strokeWidth={2.5} aria-hidden />
  //     </Button>
  //   </div>
  // );

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "grid shrink-0 grid-cols-1 border-b",
        "border-violet-300/50 dark:border-violet-500/35",
      )}
    >
      {lockedHalf}
    </div>
  );
}
